import { AvailablePaymentMethod } from "@/networking/carts.api";
import { z } from "zod";

/**
 * VGS Vault configuration interface
 */
export interface VgsVaultConfig {
  vault_id: string;
  environment: string;
  route_id: string;
  requires_3ds?: boolean;
}

/**
 * VGS credentials validation schema
 */
export const vgsCredentialsSchema = z.object({
  vaultId: z.string().min(1, "Vault ID is required"),
  environment: z.string().min(1, "Environment is required"),
  routeId: z.string().min(1, "Route ID is required"),
});

/**
 * Check if a payment method uses VGS Collect for tokenization
 */
export function usesVgsCollect(
  paymentMethod: AvailablePaymentMethod | undefined,
): boolean {
  if (!paymentMethod) return false;

  return (
    paymentMethod.type === "credit_card" &&
    "token_provider" in paymentMethod &&
    paymentMethod.token_provider === "vgs"
  );
}

/**
 * Check if VGS is enabled for any credit card payment method
 */
export function isVgsEnabled(
  paymentMethods: AvailablePaymentMethod[],
): boolean {
  return paymentMethods.some(
    (method) =>
      method.type === "credit_card" &&
      "token_provider" in method &&
      method.token_provider === "vgs",
  );
}

export function shouldInitializeVgsPaymentForm({
  requiresPaymentMethod,
  paymentMethodAutoAssignable,
  selectedPaymentMethod,
}: {
  requiresPaymentMethod: boolean;
  paymentMethodAutoAssignable: boolean;
  selectedPaymentMethod: AvailablePaymentMethod | undefined;
}): boolean {
  return (
    requiresPaymentMethod &&
    !paymentMethodAutoAssignable &&
    usesVgsCollect(selectedPaymentMethod)
  );
}

/**
 * Validate VGS configuration has required fields
 */
export function validateVgsConfig(config: VgsVaultConfig): boolean {
  try {
    vgsCredentialsSchema.parse({
      vaultId: config.vault_id,
      environment: config.environment,
      routeId: config.route_id,
    });
    return true;
  } catch {
    return false;
  }
}

export function getVgsEnvironment(environment: string): string {
  return environment === "sandbox" ? "sandbox" : "live-eu-1";
}

/**
 * Check if the application is running in a staging environment
 * based on the domain hostname or API URL
 */
export function isAppInStagingEnvironment(): boolean {
  const domainHostname = process.env.NEXT_PUBLIC_DOMAIN_HOSTNAME || "";
  const apiUrl = process.env.NEXT_PUBLIC_FLUID_API_URL || "";

  return domainHostname.includes("staging") || apiUrl.includes("staging");
}
